<script setup lang="ts">
import {
  LAWYER_TYPE_LAWYER,
  LAWYER_TYPE_ADVOCATE,
} from '@/shared/constants/lawyerTypeConstants';

import {
  ORGANIZATION_TYPE_CLUB,
  ORGANIZATION_TYPE_BUREAU,
} from '@/shared/constants/organizationTypeConstants';

import { ROLE_TYPE_USER } from '@/shared/constants/roleTypeConstants';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';
import { typeguardForString } from '@/shared/utils/typeguardForString';
import LawyerCategories from '@/entities/lawyer/ui/LawyerCategories/index.vue';
import LawyerCardInfo from '@/widgets/LawyerCard/ui/LawyerCardInfo/index.vue';
import LawyerCardActions from '@/widgets/LawyerCard/ui/LawyerCardActions.vue';
import LawyerCardFooter from '@/widgets/LawyerCard/ui/LawyerCardFooter/index.vue';
import LawyerCardGallery from '@/widgets/LawyerCard/ui/LawyerCardGallery.vue';
import CardRatingInfo from '@/widgets/LawyerCard/ui/CardRatingInfo.vue';
import type { ILawyerCardProps } from '@/widgets/LawyerCard/model/types/lawyerCard';
import type { ILawyerCategory } from '@/entities/lawyer/model/types/lawyerCategories';

const props = withDefaults(defineProps<ILawyerCardProps>(), {
  cardType: '',
  isShowCaption: false,
  isCardSlider: false,
  isLawyerPage: false,
  isCompany: false,
  isCatalog: false,
  isShowCost: false,
  isLawyerCompanyCard: false,
  isPushToFeedbacks: false,
  isArticle: false,
  isShowTitle: false,
  isEditorCard: false,
  isShowActions: true,
  isShowFooter: true,
  isShowExperience: true,
  isShowReviewsCount: true,
  isCategoriesCollapsible: false,
  maxVisibleCategories: 5,
});

const route = useRoute();
const modalStore = useModalStore();
const userStore = useUserStore();

const isAuthorizedNonUser: ComputedRef<boolean> = computed(
  () => userStore.isAuth && userStore.user?.role_type !== ROLE_TYPE_USER,
);

const isMobile: boolean = useIsMobile();

provide('lawyerInfo', props.data);
provide('isShowExperience', props.isShowExperience);
provide('isShowReviewsCount', props.isShowReviewsCount);

const isLawyerDetailPage: ComputedRef<boolean> = computed(() =>
  String(route.name).includes('detail'),
);
const isDetailpage: ComputedRef<boolean> = computed(() =>
  String(route.name).includes('lawyer-detail'),
);
const isShowSkeleton = isLawyerDetailPage.value
  ? inject<Ref<boolean>>('isShowSkeleton')
  : null;

const isShowGallery: ComputedRef<boolean> = computed(
  () =>
    Boolean(props.data?.galleryImgs?.length) &&
    isLawyerDetailPage.value &&
    !isShowSkeleton?.value &&
    !props.isCardSlider,
);

const isShowCategoriesCardInfo: ComputedRef<boolean> = computed(
  () =>
    (props.isCatalog && !isMobile) ||
    (props.isEditorCard && isMobile) ||
    (!props.isCardSlider &&
      !isMobile &&
      !props.isLawyerCompanyCard &&
      !props.isLawyerPage &&
      !props.isArticle),
);

const isShowCategories: ComputedRef<boolean> = computed(
  () =>
    (props.isCardSlider && !props.isArticle) ||
    (isMobile &&
      !props.isCatalog &&
      !props.isLawyerCompanyCard &&
      !props.isLawyerPage &&
      !props.isArticle) ||
    (isMobile && props.isCatalog && !props.isEditorCard),
);

const isShowActions: ComputedRef<boolean> = computed(
  () => props.isShowActions && !props.isLawyerCompanyCard && !props.isArticle,
);

const isShowFooter: ComputedRef<boolean> = computed(
  () =>
    props.isShowFooter &&
    (props.isShowCaption || props.isCardSlider || props.isLawyerPage || props.isArticle),
);

const categories = computed<ILawyerCategory[]>(() => {
  const category = typeguardForString(route.params.category);

  if (category && props.data.mainCategory) {
    const main = props.data.mainCategory.find((el) => el?.slug === category);
    if (main) {
      return (props.data.activityCategories ?? []).filter(
        (c) => c?.parent_id === main.id,
      );
    }
  }

  return props.data.mainCategory ?? [];
});

const imageAlt: ComputedRef<string> = computed(() => {
  let type = '';
  let name = '';
  const role = props.data?.role;
  const companyId = props.data?.company_id;

  if (role === LAWYER_TYPE_LAWYER && !companyId) {
    type = 'Юрист';
    name = `${props.data?.surname} ${props.data?.name}`;
  }
  if (role === LAWYER_TYPE_LAWYER && companyId) {
    type = 'Юридическая компания';
    name = props.data?.company?.name ?? '';
  }
  if (role === LAWYER_TYPE_ADVOCATE && !companyId) {
    type = 'Адвокат';
    name = `${props.data?.surname} ${props.data?.name}`;
  }
  if (role === LAWYER_TYPE_ADVOCATE && companyId) {
    if (props.data?.company?.organisation_type === ORGANIZATION_TYPE_CLUB) {
      type = 'Адвокатская коллегия';
    } else if (props.data?.company?.organisation_type === ORGANIZATION_TYPE_BUREAU) {
      type = 'Адвокатское бюро';
    } else {
      type = 'Адвокатская контора';
    }
    name = props.data?.company?.name ?? '';
  }

  return `${type} ${name}`;
});

const showModal = (): void => {
  if (!isDetailpage.value) return;
  modalStore.setModalState('image', true, {
    url: props.data.urlAvatar,
    alt: imageAlt.value,
  });
};
</script>

<template>
  <article
    :class="[
      'lawyer-card',
      { _mobile: isMobile },
      { _catalog: isCatalog },
      { _slider: isCardSlider },
      { _lawyer: isLawyerPage },
      { _company: isCompany },
      { _article: isArticle },
      { _editor: isEditorCard },
      { '_is-lawyer-company': isLawyerCompanyCard },
    ]"
  >
    <div v-if="isShowTitle" class="lawyer-card__title">Профиль автора статьи</div>
    <div class="lawyer-card__top">
      <CardRatingInfo
        class="lawyer-card__rating-info"
        :url-avatar="data.urlAvatar"
        :url-avatar-thumbnail="data.urlAvatarThumbnail"
        :url-avatar-thumbnail2x="data.urlAvatarThumbnail2x"
        :image-alt="imageAlt"
        :feedback-count="data.feedbackCount"
        :feedback-rating="data.feedbackRating"
        :is-detailpage="isDetailpage"
        :is-priority-image="isLawyerPage"
        :is-push-to-feedbacks="isPushToFeedbacks"
        :is-lawyer-company-card="isLawyerCompanyCard"
        @show-image="showModal"
      />
      <div class="lawyer-card__info">
        <div v-if="isShowActions" class="lawyer-card__info-actions">
          <LawyerCardActions :is-lawyer-page="isLawyerPage" />
        </div>
        <div :key="data.id" class="lawyer-card__info-content">
          <LawyerCardInfo
            :is-catalog="isCatalog"
            :is-card-slider="isCardSlider"
            :is-lawyer-page="isLawyerPage"
            :is-article="isArticle"
            :is-lawyer-company-card="isLawyerCompanyCard"
            :is-show-cost="isShowCost"
          />
          <div v-if="isShowCategoriesCardInfo" class="lawyer-card__categories">
            <LawyerCategories
              :categories="categories"
              :is-authorized-non-user="isAuthorizedNonUser"
              :is-collapsible="isCategoriesCollapsible"
              :max-visible-categories="maxVisibleCategories"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="isShowCategories" class="lawyer-card__categories">
      <LawyerCategories
        :categories="categories"
        :is-authorized-non-user="isAuthorizedNonUser"
        :is-collapsible="isCategoriesCollapsible"
        :max-visible-categories="maxVisibleCategories"
      />
    </div>
    <div v-if="isShowFooter" class="lawyer-card__footer">
      <LawyerCardFooter
        :is-show-caption="isShowCaption"
        :is-card-slider="isCardSlider"
        :is-lawyer-page="isLawyerPage"
        :is-catalog="isCatalog"
        :is-article="isArticle"
      />
    </div>
    <div v-if="isShowGallery" class="lawyer-card__gallery">
      <LawyerCardGallery :gallery="data.galleryImgs" />
    </div>
  </article>
</template>

<style lang="less">
.lawyer-card {
  .box-shadow();
  .flex-column(@size-x5);

  background-color: white;
  padding: @size-x5 @size-x5 30px @size-x5;
  border-radius: @main-size;
  width: 100%;

  &._lawyer {
    @media @1366 {
      padding: @size-x5;
    }
  }

  &._company {
    @media @desktop {
      padding: @size-x5 @size-x5 30px @size-x5;
    }
  }

  &._slider {
    display: grid;
    height: 100%;
  }

  &__top {
    display: flex;
    align-items: flex-start;
    gap: @size-x6;
  }

  &__title {
    font-size: @size-x4;
    line-height: 21px;
    font-weight: 700;
  }

  &._lawyer &__top {
    @media @1366 {
      flex-wrap: wrap;
      position: relative;
      gap: @size-x3;
    }
  }

  &._is-lawyer-company &__top {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: @main-size;
  }

  &__info {
    position: relative;
    flex: 1 1 auto;
  }

  &__info-actions {
    position: absolute;
    top: 0;
    right: 0;
  }

  &._lawyer &__info {
    @media @1366 {
      position: static;
    }
  }

  &._is-lawyer-company &__info {
    text-align: center;
    margin-left: 0;
    margin-top: @main-size;
  }

  &._is-lawyer-company &__rating-info {
    text-align: center;
  }

  &._catalog &__categories {
    margin-top: @size-x3;
  }

  &._article &__footer {
    margin-top: auto;
  }

  &._mobile {
    padding: @size-x4;

    &._lawyer {
      padding-bottom: 30px;
    }
  }

  &._mobile &__info-content {
    display: flex;
    flex-direction: column;
  }

  &._mobile._catalog &__categories {
    margin-top: 0;
  }

  &._mobile._lawyer &__top {
    flex-wrap: nowrap;
    gap: @size-x5;
  }

  &._mobile._lawyer &__info {
    margin-top: 0;
  }

  &._mobile._is-lawyer-company &__info {
    margin-left: 0;
  }

  &._mobile._is-lawyer-company &__info-content {
    display: block;
  }
}
</style>
